import { Box, Plus, Search } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "@/components/common/DataTable";
import type { ColumnDef } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { InputGroup } from "@/components/ui/Input";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { TextareaGroup } from "@/components/ui/Textarea";
import { toast } from "@/components/ui/Toast";
import { api } from "@/lib/api-client";
import { toRiskScore } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import { useTranslation } from "@/hooks/useTranslation";
import type { EntityStatus, Language, RiskLevel } from "@/types";

// ── Vendor type for dropdowns ─────────────────
interface VendorOption {
  id: string;
  name: string;
}

// ─────────────────────────────────────────────
// AppInventory — Screen 3 (gkFrA)
//
// Entity list with search, filters, pagination,
// and "+ Register Application" dialog.
// Design: §8.5 Screen 3
// ─────────────────────────────────────────────

// ── Types ─────────────────────────────────────

type EntityType = "APPLICATION" | "BUSINESS_PROCESS" | "HARDWARE" | "PERSON";

// Description/version are stored inside the entity `metadata` JSON blob
// (see AppInventory create dialog + API EntityMetadata), not at the top level.
interface EntityMetadata {
  description?: string;
  version?: string;
  [key: string]: unknown;
}

interface Entity {
  id: string;
  name: string;
  type: EntityType;
  description?: string;
  version?: string;
  metadata?: EntityMetadata;
  status: EntityStatus;
  riskLevel?: RiskLevel;
  riskScore?: number;
  createdAt: string;
}

// The API returns paginated lists as { data, meta: { total, ... } }.
interface EntitiesResponse {
  data: Entity[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

// ── Entity type badge ─────────────────────────

const ENTITY_TYPE_CLASSES: Record<EntityType, string> = {
  APPLICATION: "bg-accent-bg text-accent-text",
  BUSINESS_PROCESS: "bg-warning-bg text-warning",
  HARDWARE: "bg-k-purple-bg text-k-purple",
  PERSON: "bg-success-bg text-success",
};

function EntityTypeBadge({ type }: { type: EntityType }) {
  const { t } = useTranslation();
  const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
    APPLICATION: t("entity.types.APPLICATION"),
    BUSINESS_PROCESS: t("entity.types.BUSINESS_PROCESS"),
    HARDWARE: t("entity.types.HARDWARE"),
    PERSON: t("entity.types.PERSON"),
  };
  return (
    <span
      className={`inline-flex items-center rounded-badge px-2 py-0.5 text-[12px] font-semibold leading-none ${ENTITY_TYPE_CLASSES[type] ?? "bg-hover text-text-muted"}`}
    >
      {ENTITY_TYPE_LABELS[type] ?? type}
    </span>
  );
}

// ── Risk score bar ────────────────────────────

function RiskBar({
  score,
  level,
}: {
  score?: number;
  level?: RiskLevel;
}) {
  if (score === null || score === undefined) return <span className="text-caption text-text-muted">-</span>;

  const COLORS: Record<RiskLevel, string> = {
    LOW: "var(--success)",
    MEDIUM: "var(--warning)",
    HIGH: "var(--k-orange)",
    VERY_HIGH: "var(--k-purple)",
    CRITICAL: "var(--danger)",
  };

  const color = level ? COLORS[level] : "var(--accent)";

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-hover">
        <div
          className="h-full rounded-full"
          style={{ width: `${score}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-caption text-text-muted">{score}%</span>
    </div>
  );
}

// ── Table columns ─────────────────────────────

function buildColumns(t: (key: string) => string, language: Language): ColumnDef<Entity>[] {
  return [
    {
      key: "name",
      header: t("entity.name"),
      sortable: true,
      cell: (row) => (
        <span className="font-medium text-text-primary">{row.name}</span>
      ),
    },
    {
      key: "type",
      header: t("entity.type"),
      cell: (row) => <EntityTypeBadge type={row.type} />,
    },
    {
      key: "description",
      header: t("entity.description"),
      cell: (row) => (
        <span className="line-clamp-1 max-w-xs text-text-secondary">
          {row.metadata?.description ?? row.description ?? "-"}
        </span>
      ),
    },
    {
      key: "status",
      header: t("entity.status"),
      cell: (row) => <StatusBadge type="entity" status={row.status} />,
    },
    {
      key: "riskScore",
      header: t("entity.risk"),
      cell: (row) => <RiskBar score={row.riskScore !== undefined ? toRiskScore(row.riskScore) : undefined} level={row.riskLevel} />,
    },
    {
      key: "createdAt",
      header: t("entity.created"),
      sortable: true,
      sortAccessor: (row) => new Date(row.createdAt),
      cell: (row) => (
        <span className="text-caption text-text-muted">
          {formatDate(row.createdAt, language, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
  ];
}

// ── Create Entity Dialog ──────────────────────

type ApplicationTypeValue =
  | "WEB_APPLICATION"
  | "INFRASTRUCTURE"
  | "API"
  | "INTERNAL_TOOLING"
  | "MOBILE_APPLICATION"
  | "DESKTOP_APPLICATION"
  | "OTHER";

type BusinessCriticalityValue = "LOW" | "MEDIUM" | "HIGH" | "NA";
type DataClassificationValue = "PUBLIC" | "INTERNAL" | "CONFIDENTIAL" | "RESTRICTED" | "NA";
type DevelopmentValue = "IN_HOUSE" | "HYBRID" | "VENDOR";
type HostingValue = "ON_PREMISE" | "PRIVATE_CLOUD" | "PUBLIC_CLOUD" | "HYBRID" | "NA";

interface CreateEntityForm {
  name: string;
  applicationType: ApplicationTypeValue | "";
  description: string;
  businessCriticality: BusinessCriticalityValue | "";
  dataClassification: DataClassificationValue | "";
  development: DevelopmentValue | "";
  hosting: HostingValue | "";
  department: string;
  developmentVendorId: string;
  hostingVendorId: string;
}

const EMPTY_FORM: CreateEntityForm = {
  name: "",
  applicationType: "",
  description: "",
  businessCriticality: "",
  dataClassification: "",
  development: "",
  hosting: "",
  department: "",
  developmentVendorId: "",
  hostingVendorId: "",
};

const APPLICATION_TYPE_LABELS: Record<ApplicationTypeValue, string> = {
  WEB_APPLICATION: "Web Application",
  INFRASTRUCTURE: "Infrastructure",
  API: "API",
  INTERNAL_TOOLING: "Internal Tooling",
  MOBILE_APPLICATION: "Mobile Application",
  DESKTOP_APPLICATION: "Desktop Application",
  OTHER: "Other",
};

interface CreateEntityDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}

function CreateEntityDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateEntityDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateEntityForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vendors, setVendors] = useState<VendorOption[]>([]);

  // Load vendors when dialog opens
  useEffect(() => {
    if (!open) return;
    api
      .get<{ data: VendorOption[]; meta: unknown }>("/vendors?pageSize=100")
      .then((res) => setVendors(res.data ?? []))
      .catch(() => setVendors([]));
  }, [open]);

  function validate(): boolean {
    const next: { name?: string } = {};
    if (!form.name.trim()) next.name = t("entity.registerDialog.nameRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        type: "APPLICATION",
        ...(form.description.trim() ? { metadata: { description: form.description.trim() } } : {}),
        ...(form.applicationType ? { applicationType: form.applicationType } : {}),
        ...(form.businessCriticality ? { businessCriticality: form.businessCriticality } : {}),
        ...(form.dataClassification ? { dataClassification: form.dataClassification } : {}),
        ...(form.development ? { development: form.development } : {}),
        ...(form.hosting ? { hosting: form.hosting } : {}),
        ...(form.department.trim() ? { department: form.department.trim() } : {}),
      };

      const created = await api.post<{ data: { id: string } }>("/entities", body);
      const entityId = created.data?.id;

      // Link vendor relationships if vendors were selected
      if (entityId) {
        const vendorLinks: Array<{ vendorId: string; role: string }> = [];
        if (form.developmentVendorId && (form.development === "HYBRID" || form.development === "VENDOR")) {
          vendorLinks.push({ vendorId: form.developmentVendorId, role: "DEVELOPMENT" });
        }
        if (form.hostingVendorId && form.hosting !== "NA" && form.hosting !== "" && form.hosting !== "ON_PREMISE") {
          vendorLinks.push({ vendorId: form.hostingVendorId, role: "HOSTING" });
        }
        for (const link of vendorLinks) {
          try {
            await api.post(`/entities/${entityId}/vendors`, link);
          } catch {
            // Non-fatal — entity was created, vendor link failed
          }
        }
      }

      toast.success(t("entity.registerDialog.successTitle"), `"${form.name}" has been added.`);
      setForm(EMPTY_FORM);
      setErrors({});
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(
        t("entity.registerDialog.failTitle"),
        err instanceof Error ? err.message : "Unknown error"
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    if (isSubmitting) return;
    setForm(EMPTY_FORM);
    setErrors({});
    onOpenChange(false);
  }

  const showDevVendor = form.development === "HYBRID" || form.development === "VENDOR";
  const showHostingVendor = form.hosting !== "" && form.hosting !== "NA" && form.hosting !== "ON_PREMISE";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("entity.registerDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("entity.registerDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Application Name */}
          <InputGroup
            label={t("entity.name")}
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder={t("entity.registerDialog.namePlaceholder")}
            errorText={errors.name}
          />

          {/* Application Type */}
          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] font-medium text-text-secondary">
              Application Type
            </label>
            <SelectRoot
              value={form.applicationType}
              onValueChange={(v) => setForm((f) => ({ ...f, applicationType: v as ApplicationTypeValue }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type..." />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(APPLICATION_TYPE_LABELS) as ApplicationTypeValue[]).map((k) => (
                  <SelectItem key={k} value={k}>{APPLICATION_TYPE_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </div>

          {/* Description */}
          <TextareaGroup
            label={t("entity.description")}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder={t("entity.registerDialog.descriptionPlaceholder")}
            rows={3}
          />

          {/* Business Criticality */}
          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] font-medium text-text-secondary">Business Criticality</label>
            <SelectRoot
              value={form.businessCriticality}
              onValueChange={(v) => setForm((f) => ({ ...f, businessCriticality: v as BusinessCriticalityValue }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select criticality..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LOW">Low</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="NA">N/A</SelectItem>
              </SelectContent>
            </SelectRoot>
          </div>

          {/* Data Classification */}
          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] font-medium text-text-secondary">Data Classification</label>
            <SelectRoot
              value={form.dataClassification}
              onValueChange={(v) => setForm((f) => ({ ...f, dataClassification: v as DataClassificationValue }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select classification..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PUBLIC">Public</SelectItem>
                <SelectItem value="INTERNAL">Internal</SelectItem>
                <SelectItem value="CONFIDENTIAL">Confidential</SelectItem>
                <SelectItem value="RESTRICTED">Restricted</SelectItem>
                <SelectItem value="NA">N/A</SelectItem>
              </SelectContent>
            </SelectRoot>
          </div>

          {/* Development */}
          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] font-medium text-text-secondary">Development</label>
            <SelectRoot
              value={form.development}
              onValueChange={(v) => setForm((f) => ({ ...f, development: v as DevelopmentValue, developmentVendorId: "" }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select development..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="IN_HOUSE">In-house</SelectItem>
                <SelectItem value="HYBRID">Hybrid</SelectItem>
                <SelectItem value="VENDOR">Vendor</SelectItem>
              </SelectContent>
            </SelectRoot>
          </div>

          {/* Development Vendor — shown when HYBRID or VENDOR */}
          {showDevVendor && (
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">Development Vendor</label>
              <select
                className="h-9 w-full rounded-control border border-border-default bg-input px-3 text-[14px] text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                value={form.developmentVendorId}
                onChange={(e) => setForm((f) => ({ ...f, developmentVendorId: e.target.value }))}
              >
                <option value="">Select vendor...</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Hosting */}
          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] font-medium text-text-secondary">Hosting</label>
            <SelectRoot
              value={form.hosting}
              onValueChange={(v) => setForm((f) => ({ ...f, hosting: v as HostingValue, hostingVendorId: "" }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select hosting..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ON_PREMISE">On-premise</SelectItem>
                <SelectItem value="PRIVATE_CLOUD">Private Cloud</SelectItem>
                <SelectItem value="PUBLIC_CLOUD">Public Cloud</SelectItem>
                <SelectItem value="HYBRID">Hybrid</SelectItem>
                <SelectItem value="NA">N/A</SelectItem>
              </SelectContent>
            </SelectRoot>
          </div>

          {/* Hosting Vendor — shown when hosting is not NA */}
          {showHostingVendor && (
            <div className="flex flex-col gap-[6px]">
              <label className="text-[14px] font-medium text-text-secondary">Hosting Vendor</label>
              <select
                className="h-9 w-full rounded-control border border-border-default bg-input px-3 text-[14px] text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                value={form.hostingVendorId}
                onChange={(e) => setForm((f) => ({ ...f, hostingVendorId: e.target.value }))}
              >
                <option value="">Select vendor...</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Department */}
          <InputGroup
            label="Department"
            value={form.department}
            onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
            placeholder="e.g. Engineering, Finance..."
          />

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {t("entity.newEntity")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ────────────────────────────

export function AppInventory() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const [entities, setEntities] = useState<Entity[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<EntityStatus | "">("");
  const [createOpen, setCreateOpen] = useState(false);

  // Debounce search
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  const fetchEntities = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (statusFilter) params.set("status", statusFilter);
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));

      const res = await api.get<EntitiesResponse>(`/entities?${params.toString()}`);
      setEntities(res.data ?? []);
      setTotal(res.meta?.total ?? 0);
      setIsError(false);
    } catch {
      // WP20 (S-138/S-147): a failed fetch must not masquerade as an empty
      // "No entities registered" onboarding state — surface it with a retry.
      setEntities([]);
      setTotal(0);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, statusFilter, page]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  const columns = buildColumns(t, language);
  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-[24px] font-semibold text-text-primary">
            {t("entity.title")}
          </h1>
          <p className="text-body text-text-secondary">
            {t("entity.subtitle")}
          </p>
        </div>
        <Button
          leadingIcon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          {t("entity.newEntity")}
        </Button>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        <div className="max-w-xs flex-1">
          <Input
            leadingIcon={<Search className="h-4 w-4" />}
            placeholder={t("entity.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Status filter */}
        <SelectRoot
          value={statusFilter || "ALL"}
          onValueChange={(v) => {
            setStatusFilter((v === "ALL" ? "" : v) as EntityStatus | "");
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder={t("common.allStatuses")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t("common.allStatuses")}</SelectItem>
            <SelectItem value="ACTIVE">{t("status.active")}</SelectItem>
            <SelectItem value="ARCHIVED">{t("status.archived")}</SelectItem>
            <SelectItem value="DECOMMISSIONED">{t("status.decommissioned")}</SelectItem>
          </SelectContent>
        </SelectRoot>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={entities}
        isLoading={isLoading}
        isError={isError}
        onRetry={fetchEntities}
        onRowClick={(row) => navigate(`/apps/${row.id}`)}
        emptyState={{
          icon: Box,
          title: t("entity.noEntitiesRegistered"),
          description: t("entity.noEntitiesRegisteredDesc"),
          action: {
            label: `+ ${t("entity.newEntity")}`,
            onClick: () => setCreateOpen(true),
          },
        }}
        page={page}
        totalPages={totalPages}
        totalItems={total}
        pageSize={pageSize}
        onPageChange={setPage}
      />

      {/* Create dialog */}
      <CreateEntityDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={fetchEntities}
      />
    </>
  );
}
